import { CSSProperties, useMemo, useState } from 'react';
import { Box, Typography, Grid, Button, Checkbox, MenuItem } from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import { AccessTime, InfoOutlined, Close } from '@mui/icons-material';
import { WorkRestProfile, WorkRestIntensity, WorkRestMatrix } from '../../../Services/API/workRestApi';
import { ProgressBar } from '../ProgressBar';
import { PulseIcon } from '../../../Assets/WorkRest/Pulse';
import { ShieldIcon } from '../../../Assets/WorkRest/Shield';
import { Observation } from '../../../Services/API/observationsApi';
import { getCurrentObservationValue, getLabels, getWorkRestMatrix } from './WorkRestHelpers';
import { Card, Select, MultiSelect, SelectOption } from '@perry-weather/component-library';
import { WorkRestConditionSection } from './WorkRestCondition';
import { RiskProfile } from '../../../Services/API/riskProfileApi';
import { RiskLevelBar } from './RiskProfile/RiskLevelBar';
import { useHourlyForecastByHoursQuery } from '../../../Services/API/forecastApi';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import WorkRestForecastChart from './ForecastChart/WorkRestForecastChart';
import WorkRestAdjustmentCard from './WorkRestAdjustmentCard';
import PercentIcon from '@mui/icons-material/Percent';
import { defineWorkRestIcon } from './WorkRestCard';

type WorkRestDetailsProps = {
  currentObservation: Observation;
  workRestProfile: WorkRestProfile;
  selectedIntensity: WorkRestIntensity;
  onIntensityChange: (intensity: WorkRestIntensity) => void;
  selectedModifiers: number[];
  onModifierChange: (modifiers: number[]) => void;
  handleClose: () => void;
  riskProfile: RiskProfile;
  locationId: string;
};

const getWorkRestTimeSplit = (
  profile?: WorkRestProfile,
  matrix?: WorkRestMatrix,
  durationType: 'unit' | 'percent' = 'unit'
) => {
  if (!profile || !matrix) return { work: '--', rest: '--', unit: '' };

  if (durationType === 'percent') {
    return { work: matrix.workPercent, rest: 100 - matrix.workPercent, unit: 'ratio' };
  }

  if (profile.durationUnit === 'Hour') {
    const work = 60 * (matrix.workPercent / 100);
    const rest = 60 - work;
    return { work, rest, unit: 'minutes' };
  }

  return { work: '--', rest: '--', unit: '' };
};

const metricDescriptions: Record<string, string> = {
  Temperature: `Heat stress risk increases with both environmental conditions and physical effort. Temperature reflects only the air temperature in shaded conditions, and does not account for humidity, wind, or sun exposure. Intense work, poor hydration, and heavy clothing can also increase risk. Monitor temperature regularly and adjust work/rest schedules to prevent heat-related illness.`,
  
  'Heat Index': `Heat stress risk increases with both environmental conditions and physical effort. The Heat Index reflects how hot it feels by combining air temperature and humidity, but does not account for direct sun exposure or wind. Intense work, poor hydration, and heavy clothing can also increase risk. Monitor the Heat Index regularly and adjust work/rest schedules to prevent heat-related illness.`,
  
  WBGT: `Heat stress risk increases with both environmental conditions and physical effort. Wet Bulb Globe Temperature (WBGT) is the most accurate way to measure risk, factoring in temperature, humidity, wind, and solar radiation. Intense work, poor hydration, and heavy clothing can also increase heat stress. Monitor WBGT regularly and adjust work/rest schedules to prevent heat-related illnesses.`,
};
const getInfoCardText = (workRestProfile: WorkRestProfile) => metricDescriptions[workRestProfile.primaryMetric] ?? '';


const useStyles = (theme: Theme) => ({
  card: {
    card: {
      backgroundColor: theme.palette.mode === 'dark' ? '#252B3C' : '#F7F8FA',
      marginTop: 3,
    },
  },
  select: {
    value: {
      flex: 1,
      textAlign: 'center',
    },
    generalStyle: {
      backgroundColor: theme.palette.mode === 'dark' ? '#252B3C' : '#F7F8FA',
      border: '1px solid #495361',
      borderRadius: 5,
      width: 160,
      height: 35,
      '& .MuiSelect-icon': {
        color: theme.palette.text.primary, 
      },
      '& .MuiSelect-select': {
        padding: '4px 32px 4px 10px !important'
      },
      '& .MuiSelect-select:focus': {
        border: 'none'
      },
    },
    multiSelect: {
      '&.Mui-selected': {
        backgroundColor: theme.palette.mode === 'dark' ? '#26404d' : '#f3f8fb',
      },
      '&.Mui-focusVisible': {
        backgroundColor: 'transparent',
      },
      '&:hover': {
        backgroundColor: theme.palette.action.hoverSecondary,
      },
    }
  },
  intensity: {
    container: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingLeft: '24px',
      paddingRight: '8px',
      paddingTop: '4px',
      paddingBottom: '4px',
      borderRadius: 2,
      overflow: 'hidden',
      cursor: 'pointer',
    },
    bar: {
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: 0,
      width: 8,
    },
  },
});

export function WorkRestDetailsModal({
  currentObservation,
  workRestProfile,
  selectedIntensity,
  onIntensityChange,
  selectedModifiers,
  onModifierChange,
  handleClose,
  riskProfile,
  locationId,
}: WorkRestDetailsProps) {
  const theme = useTheme();
  const styles = useStyles(theme);
  const isDarkTheme = theme.palette.mode === 'dark';
  const iconColor = isDarkTheme ? theme.palette.text.primary : theme.palette.text.secondary;
  const locationRequest = locationId ? { locationId, hours: 71 } : skipToken;
  const { data: hourlyForecast } = useHourlyForecastByHoursQuery(locationRequest);

  const observationValue = getCurrentObservationValue(currentObservation, workRestProfile);
  const totalAdjustment =
    workRestProfile?.modifiers
      ?.filter(mod => selectedModifiers.includes(mod.id))
      .reduce((sum, mod) => sum + mod.adjustment, 0) ?? 0;
  const adjustedObservation = observationValue! + totalAdjustment;
  const currentMatrix = getWorkRestMatrix(workRestProfile, adjustedObservation, selectedIntensity);
  const [durationType, setDurationType] = useState<'percent' | 'unit'>('unit');
  const { work, rest, unit } = getWorkRestTimeSplit(workRestProfile, currentMatrix, durationType);

  const handleIntensityChange = (value: string) => {
    const selectedIntensity = workRestProfile.intensities.find(i => i.id === parseInt(value));
    if (!selectedIntensity) return;
    onIntensityChange?.(selectedIntensity);
  };

  const activeModifiers = workRestProfile?.modifiers.filter(mod => selectedModifiers.includes(mod.id));
  const total = activeModifiers?.reduce((acc, mod) => acc + mod.adjustment, 0) ?? 0;
  const ModifierLabel = (
    <Box display='flex'>
      <ShieldIcon width={24} height={24} />
      <Typography sx={styles.select.value as CSSProperties}>
        {total === 0 ? 'No PPE' : `+${total.toFixed(1)}°`}
      </Typography>
    </Box>
  );

  const modifierOptions: SelectOption[] = useMemo(
    () =>
      workRestProfile?.modifiers.map(modifier => ({
        value: modifier.id.toString(),
        text: (
          <Grid container direction='column'>
            <Typography>{modifier.name}</Typography>
            <Typography fontSize='14px' color={theme.palette.text.secondary}>
              +&nbsp;{modifier.adjustment}°F
            </Typography>
          </Grid>
        ),
      })) ?? [],
    [workRestProfile]
  );

  const intensityOptions: SelectOption[] = useMemo(
    () =>
      workRestProfile?.intensities.map(intensity => ({
        value: intensity.id.toString(),
        text: intensity.name,
      })) ?? [],
    [workRestProfile]
  );

  const handleModifierChange = (values: string[]) => {
    onModifierChange?.(values.map(value => parseInt(value)));
  };

  const handleModifierSelection = (modifierId: number) => {
    const isAlreadySelected = selectedModifiers.includes(modifierId);
    const updated = isAlreadySelected
      ? selectedModifiers.filter(id => id !== modifierId)
      : [...selectedModifiers, modifierId];

    onModifierChange(updated);
  };

  const handleIntensitySelection = (intensityId: number) => {
    const found = workRestProfile?.intensities.find(i => i.id === intensityId);
    if (found) onIntensityChange(found);
  };

  const icon = defineWorkRestIcon(workRestProfile, iconColor);

  return (
    <Box>
      <Box display='flex' flexDirection='column'>
        <Box display='flex' justifyContent='space-between'>
          <Typography variant='h6' fontWeight='bold'>
            Heat Stress Details
          </Typography>
          <Close style={{ cursor: 'pointer' }} onClick={handleClose} />
        </Box>

        {/* Adjusted readings and suggestion */}
        <Card sx={{ ...styles.card, content: { flexDirection: 'column' } }}>
          <Box display='flex' alignItems='center' gap={1} mb={1.4}>
            {icon}
            <Typography variant='body1' fontWeight='bold'>
              {workRestProfile?.primaryMetric}
            </Typography>

            <Box
              sx={{
                ml: 'auto',
                backgroundColor: isDarkTheme ? styles.card.card.backgroundColor : '#ebedf1',
                borderRadius: 10,
                p: 0.1,
                border: '1px solid #495361',
              }}>
              <Button
                onClick={() => setDurationType(prev => (prev === 'unit' ? 'percent' : 'unit'))}
                sx={{
                  minWidth: 'auto',
                  padding: '2px 2px 2px 2px',
                  gap: 0,
                  display: 'flex',
                  alignItems: 'center',
                  borderRadius: 10,
                }}>
                <Box
                  display='flex'
                  gap={1}
                  sx={{
                    backgroundColor: durationType === 'unit' ? (isDarkTheme ? '#767F8A' : '#ABB2B8') : 'transparent',
                    borderRadius: 20,
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                    padding: '3px 8px',
                  }}>
                  <AccessTime
                    sx={{
                      color: durationType === 'unit' ? theme.palette.text.primary : '#767F8A',
                      width: 20,
                      height: 20,
                    }}
                  />
                </Box>
                <Box
                  display='flex'
                  gap={1}
                  sx={{
                    backgroundColor: durationType === 'percent' ? (isDarkTheme ? '#767F8A' : '#ABB2B8') : 'transparent',
                    borderRadius: 20,
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    padding: '3px 8px',
                  }}>
                  <PercentIcon
                    sx={{
                      color: durationType === 'percent' ? theme.palette.text.primary : '#767F8A',
                      width: 20,
                      height: 20,
                    }}
                  />
                </Box>
              </Button>
            </Box>
          </Box>
          <Grid container gap={2} marginTop={1} direction='row'>
            <Box display='flex' flexDirection='column' alignItems='center' flex={1}>
              <WorkRestConditionSection adjustedValue={adjustedObservation!} actualValue={observationValue} />
              <Box height={18} />
              <MultiSelect
                options={modifierOptions}
                placeholder='+0.0°F'
                renderValue={ModifierLabel}
                onChange={handleModifierChange}
                value={selectedModifiers.map(String)}
                sx={{
                  select: styles.select.generalStyle,
                  menu: {
                    PaperProps: {
                      sx: {
                        backgroundColor: theme.palette.background.default,
                        backgroundImage: 'none',
                        opacity: 1
                      },
                    },
                  },
                }}
                renderMenuItem={(option, selectedValues) => {
                  const selected = selectedValues as string[];
                  const isSelected = selected.includes(option.value as string);
                  return (
                    <MenuItem key={option.value as string} value={option.value as string} sx={styles.select.multiSelect}>
                      <Box display="flex" alignItems="start">
                        <Checkbox checked={isSelected} style={{ padding: '3px 0 0 0', marginRight: 8 }} size='small'/>
                        {option.text}
                      </Box>
                    </MenuItem>
                  );
                }}
              />
            </Box>
            <Box style={{ width: 1, backgroundColor: theme.palette.divider, opacity: 0.4 }}></Box>
            <Box display='flex' flexDirection='column' alignItems='center' flex={1}>
              <Typography variant='h3' fontWeight='bold'>
                {durationType === 'unit' ? `${work}/${rest}` : `${work}%`}
              </Typography>
              <Typography variant='body1' color='textSecondary'>
                Work / Rest ({unit})
              </Typography>
              <Box height={18} />
              <Select
                options={intensityOptions}
                placeholder='Intensity'
                renderValue={option => (
                  <Box display='flex'>
                    <PulseIcon width={24} height={24} />
                    <Typography sx={styles.select.value as CSSProperties}>{option?.text}</Typography>
                  </Box>
                )}
                onChange={handleIntensityChange}
                value={selectedIntensity.id.toString()}
                sx={styles.select.generalStyle}
              />
            </Box>
          </Grid>
        </Card>
      </Box>

      {/*Risk Level bar*/}
      {workRestProfile.useRiskProfile && riskProfile && (
        <Card sx={{ ...styles.card, content: { flexDirection: 'column' } }}>
          <Grid container gap={2} marginTop={1} direction='row'>
            <RiskLevelBar riskLevels={riskProfile.riskLevels} currentValue={adjustedObservation} />
          </Grid>
        </Card>
      )}

      {/* Intensity Breakdown  */}
      <Card sx={{ ...styles.card, content: { padding: '16px 0px' } }}>
        <Box display='flex' alignItems='center' gap={1} mb={2} paddingLeft={3}>
          <AccessTime fontSize='small' sx={{ color: iconColor }} />
          <Typography fontWeight='bold'>Work / Rest Cycles</Typography>
        </Box>

        <Box display='flex' flexDirection='column' gap={1}>
          {workRestProfile?.intensities.map(intensity => {
            const matrix = getWorkRestMatrix(workRestProfile, adjustedObservation, intensity);
            const workPercent = matrix?.workPercent ?? 0;
            const { leftLabel, rightLabel } = getLabels(workRestProfile, matrix, durationType);

            return (
              <Box
                key={intensity.id}
                sx={{
                  ...styles.intensity.container,
                  backgroundColor: intensity.id === selectedIntensity?.id ? '#314572' : 'transparent',
                  position: 'relative',
                }}
                onClick={() => onIntensityChange(intensity)}>
                <Box
                  sx={styles.intensity.bar}
                  bgcolor={intensity.id === selectedIntensity?.id ? '#B2EDFF' : 'transparent'}
                />

                <Typography
                  sx={{
                    minWidth: 100,
                    color: intensity.id === selectedIntensity?.id ? 'white' : theme.palette.text.primary,
                  }}>
                  {intensity.name}
                </Typography>
                <Box flex={1}>
                  <ProgressBar
                    progress={workPercent}
                    leftLabel={leftLabel}
                    rightLabel={rightLabel}
                    primaryColor={{ background: '#B2EDFF', text: '#125D6E' }}
                    secondaryColor={{ background: '#036980', text: '#A9E5F7' }}
                  />
                </Box>
              </Box>
            );
          })}
        </Box>

        <Box display='flex' justifyContent='flex-end' alignItems='center' gap={1} mt={2} paddingRight={2}>
          <Box width={10} height={10} borderRadius='50%' bgcolor='#B2EDFF' />
          <Typography>Work</Typography>
          <Box width={10} height={10} borderRadius='50%' bgcolor='#036980' />
          <Typography>Rest</Typography>
        </Box>
      </Card>

      {/*Forecast Chart*/}
      <Card sx={styles.card}>
        {hourlyForecast && (
          <Box sx={{ mb: 2 }}>
            <WorkRestForecastChart
              data={hourlyForecast}
              riskProfile={riskProfile}
              totalAdjustment={totalAdjustment}
              currentObservationValue={adjustedObservation}
              currentIntensity={selectedIntensity}
              workRestMatrix={currentMatrix!}
              durationType={durationType}
              workRestProfile={workRestProfile}
            />
          </Box>
        )}
      </Card>

      {/* Adjustments card */}
      <Card sx={styles.card}>
        <WorkRestAdjustmentCard
          workRestProfile={workRestProfile}
          currentIntensity={selectedIntensity}
          currentModifiers={workRestProfile.modifiers.filter(mod => selectedModifiers.includes(mod.id))}
          handleIntensityChange={handleIntensitySelection}
          handleModifierChange={handleModifierSelection}
          totalAdjustment={totalAdjustment}
        />
      </Card>

      {/* Info card */}
      <Card sx={styles.card}>
        <Box display='flex' alignItems='center' gap={1}>
          <InfoOutlined fontSize='small' sx={{ color: iconColor }} />
          <Typography variant='h6' fontWeight='bold'>
            Understanding Heat Stress
          </Typography>
        </Box>

        <Typography mt={2}>{getInfoCardText(workRestProfile)}</Typography>
      </Card>
    </Box>
  );
}
